import { useState } from 'react';
import { Trash2, PackagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { stockApi } from '../lib/api';
import { coincideProducto } from '../lib/buscar';

// 2026-11-02 (pedido del dueño): un producto se marca como KIT en su edición y se arma con
// piezas del almacén. El kit no tiene stock propio: sus kits armables salen del stock de las
// piezas. Ingresar kits suma a cada pieza. Lo usa la edición de Almacén (StockCompleto).
export interface PiezaKit { producto_id: string; nombre: string; cantidad: number; stock: number; precio_venta: number }
export interface EstadoKit { es_kit: boolean; componentes: PiezaKit[]; sumar_stock_actual: boolean }

export const estadoKitDe = (p: any): EstadoKit => ({
  es_kit: !!p.es_kit,
  componentes: (p.kit_componentes || []).map((c: any) => ({
    producto_id: String(c.componente_id), nombre: c.nombre, cantidad: Number(c.cantidad),
    stock: Number(c.stock) || 0, precio_venta: Number(c.precio_venta) || 0,
  })),
  sumar_stock_actual: true,
});

export const firmaKit = (k: EstadoKit) =>
  JSON.stringify([k.es_kit, k.es_kit ? k.componentes.map((c) => [c.producto_id, c.cantidad]) : []]);

export const armables = (piezas: PiezaKit[]) =>
  piezas.length ? Math.max(0, Math.floor(Math.min(...piezas.map((c) => (c.cantidad > 0 ? c.stock / c.cantidad : 0))) + 1e-9)) : 0;

interface Props {
  producto: any;        // el producto que se está editando (tal como estaba guardado)
  productos: any[];     // catálogo completo, para buscar piezas
  kit: EstadoKit;
  onChange: (k: EstadoKit) => void;
  onIngresado: () => void;
  precioActual?: number;                // precio de venta escrito en la edición (aún sin guardar)
  onUsarPrecio?: (precio: number) => void;
}

export default function KitEditor({ producto, productos, kit, onChange, onIngresado, precioActual, onUsarPrecio }: Props) {
  const [busca, setBusca] = useState('');
  const [ingresar, setIngresar] = useState('');
  const [obsIngreso, setObsIngreso] = useState('');
  const [guardando, setGuardando] = useState(false);
  const yaEraKit = !!producto.es_kit;
  const stockPropio = Number(producto.stock) || 0;

  const texto = busca.trim().toLowerCase();
  const candidatos = texto.length < 2 ? [] : productos.filter((p) =>
    p.id !== producto.id && !p.es_kit && !p.eliminado && (p.tipo || 'venta') === 'venta' &&
    !kit.componentes.some((c) => c.producto_id === String(p.id)) &&
    coincideProducto(busca, p)
  ).slice(0, 8);

  const agregar = (p: any) => {
    onChange({ ...kit, componentes: [...kit.componentes, { producto_id: String(p.id), nombre: p.nombre, cantidad: 1,
      stock: Number(p.stock) || 0, precio_venta: Number(p.precio_venta) || 0 }] });
    setBusca('');
  };
  const cambiarCantidad = (i: number, v: string) =>
    onChange({ ...kit, componentes: kit.componentes.map((c, j) => (j === i ? { ...c, cantidad: parseFloat(v) || 0 } : c)) });
  const quitar = (i: number) => onChange({ ...kit, componentes: kit.componentes.filter((_, j) => j !== i) });

  const sumaPiezas = kit.componentes.reduce((a, c) => a + c.precio_venta * c.cantidad, 0);
  const precioKit = Number(precioActual ?? producto.precio_venta) || 0;
  const sumaRedondeada = Math.round(sumaPiezas * 100) / 100;
  // 2026-11-04: se convirtió en kit la propia zapata 4710X (el producto principal) y se perdió la
  // zapata sola. Si se marca como kit un producto que no se llama KIT/JUEGO, se avisa.
  const pareceProductoPrincipal = !yaEraKit && !/^\s*(kit|juego)\b/i.test(producto.nombre || '');

  const ingresarKits = async () => {
    const n = parseInt(ingresar, 10);
    if (!(n > 0)) return toast.error('Escribe cuántos kits llegaron (número entero)');
    setGuardando(true);
    try {
      const r = await stockApi.ingresarKits(producto.id, { cantidad: n, observacion: obsIngreso.trim() || undefined });
      toast.success(`Ingresaron ${n} kit(s): se sumaron sus piezas al almacén. Kits armables: ${r.disponibles}`);
      setIngresar(''); setObsIngreso('');
      onIngresado();
    } catch (e: any) {
      toast.error(e.message || 'No se pudo ingresar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${kit.es_kit ? 'border-purple-300 bg-purple-50' : 'border-gray-200'}`}>
      <label className="flex items-center gap-2 font-medium text-gray-800 cursor-pointer">
        <input type="checkbox" checked={kit.es_kit} onChange={(e) => onChange({ ...kit, es_kit: e.target.checked })} className="w-4 h-4" />
        Es un KIT (formado por piezas del almacén)
      </label>
      {kit.es_kit && (
        <div className="mt-3 space-y-3">
          {pareceProductoPrincipal && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded p-3 text-sm text-red-900 space-y-1">
              <p className="font-semibold">¡Ojo! ¿"{producto.nombre}" también se vende solo?</p>
              <p>Si es el producto principal del kit (por ejemplo, la zapata), <b>no lo conviertas en kit</b>: dejarías de poder
                venderlo solo y sus unidades se pasarían a las piezas.</p>
              <p>Lo correcto: desmarca esta casilla, crea un <b>producto nuevo</b> llamado <b>"KIT …"</b> con stock 0, márcalo como kit
                y agrega como piezas <b>este producto + sus accesorios</b>.</p>
            </div>
          )}
          <p className="text-xs text-gray-600">
            El kit no tiene stock propio: vender un kit descuenta todas sus piezas, y vender una pieza sola es una venta normal.
          </p>
          <div className="space-y-2">
            {kit.componentes.map((c, i) => (
              <div key={c.producto_id} className="flex items-center gap-2 bg-white border rounded p-2">
                <input type="number" min="0.01" step="0.01" value={c.cantidad || ''} onChange={(e) => cambiarCantidad(i, e.target.value)}
                       className="w-20 p-1 border rounded text-center" title="Cantidad de esta pieza en cada kit" />
                <span className="text-sm text-gray-500">×</span>
                <span className="flex-1 text-sm">{c.nombre}</span>
                <span className="text-xs text-gray-500 whitespace-nowrap">hay {c.stock}</span>
                <button type="button" onClick={() => quitar(i)} className="text-red-500 hover:text-red-700 p-1" title="Quitar pieza">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {kit.componentes.length === 0 && <p className="text-sm text-amber-700">Agrega las piezas que forman el kit.</p>}
          </div>
          <div className="relative">
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar pieza por nombre o código para agregarla…"
                   className="w-full p-2 border rounded text-sm" />
            {candidatos.length > 0 && (
              <div className="absolute z-10 left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                {candidatos.map((p) => (
                  <button key={p.id} type="button" onClick={() => agregar(p)} className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b last:border-0 text-sm">
                    <div className="font-medium">{p.nombre}</div>
                    <div className="text-xs text-gray-500">Stock: {Number(p.stock) || 0} · S/ {(Number(p.precio_venta) || 0).toFixed(2)}{p.codigo ? ` · ${p.codigo}` : ''}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {kit.componentes.length > 0 && (
            <div className="text-sm text-gray-700 flex flex-wrap gap-x-6 gap-y-1">
              <span>Kits armables con el stock de hoy: <b>{armables(kit.componentes)}</b></span>
              <span>Piezas sueltas suman: <b>S/ {sumaPiezas.toFixed(2)}</b> · precio del kit: <b>S/ {precioKit.toFixed(2)}</b></span>
              {onUsarPrecio && sumaRedondeada > 0 && Math.abs(sumaRedondeada - precioKit) >= 0.005 && (
                <button type="button" onClick={() => onUsarPrecio(sumaRedondeada)}
                        className="text-purple-700 border border-purple-300 bg-white hover:bg-purple-100 rounded px-2 py-0.5 text-xs font-medium">
                  Usar S/ {sumaRedondeada.toFixed(2)} como precio del kit
                </button>
              )}
            </div>
          )}
          {!yaEraKit && stockPropio > 0 && (
            <label className="flex items-start gap-2 text-sm bg-white border border-amber-300 rounded p-2">
              <input type="checkbox" checked={kit.sumar_stock_actual} className="mt-1"
                     onChange={(e) => onChange({ ...kit, sumar_stock_actual: e.target.checked })} />
              <span>Hoy hay <b>{stockPropio}</b> de este producto. Pasarlos a sus piezas (a cada pieza se le suma {stockPropio} × su cantidad).
                Si no lo marcas, esas {stockPropio} unidades se dejan de contar. <b>Márcalo solo si son kits completos ya armados</b>;
                si son el producto principal suelto, no lo conviertas en kit (mira el aviso rojo de arriba).</span>
            </label>
          )}
          {yaEraKit && (
            <div className="bg-white border rounded p-2 space-y-2">
              <p className="text-sm font-medium flex items-center gap-1"><PackagePlus className="w-4 h-4" /> Ingresar kits que llegaron armados</p>
              <div className="flex flex-wrap gap-2">
                <input type="number" min="1" step="1" value={ingresar} onChange={(e) => setIngresar(e.target.value)} placeholder="Cantidad"
                       className="w-28 p-2 border rounded text-sm" />
                <input value={obsIngreso} onChange={(e) => setObsIngreso(e.target.value)} placeholder="Observación (opcional)" maxLength={300}
                       className="flex-1 min-w-[10rem] p-2 border rounded text-sm" />
                <button type="button" disabled={guardando} onClick={ingresarKits}
                        className="bg-purple-600 text-white px-3 py-2 rounded text-sm disabled:opacity-50">Ingresar</button>
              </div>
              <p className="text-xs text-gray-500">Suma a cada pieza la cantidad × los kits que ingresan. Queda en Auditoría.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
